/**
 * The one place the refresh token is persisted.
 *
 * @remarks
 * A single swap point on purpose. `localStorage` is readable by any script that manages to
 * run on this origin, so this is an accepted XSS trade-off rather than a good idea:
 * the access token stays in memory precisely because it is the more dangerous of the two,
 * and the refresh token rotates on every use, so a stolen one is single-use and its reuse
 * revokes the whole family server-side.
 *
 * The documented migration is an httpOnly cookie behind a BFF
 * (`frontend-standards.md` section 5). When that happens, only this file changes.
 */

const STORAGE_KEY = 'pc.refresh';

export const tokenStorage = {
  get(): string | null {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch {
      // Private mode, or storage disabled by policy. Treated as "no session" rather than
      // as a crash: the user can still sign in, they just will not be remembered.
      return null;
    }
  },

  set(token: string): void {
    try {
      window.localStorage.setItem(STORAGE_KEY, token);
    } catch {
      // Same reasoning. Losing persistence is a degraded experience, not a failure.
    }
  },

  clear(): void {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Nothing to do; the token was never stored.
    }
  },
};
